// src/pages/Usuarios.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/Usuarios.css';

const ROLES = [
  { id: 'Cajero', descripcion: 'Factura, consulta inventario y reportes del día.' },
  { id: 'Mesero', descripcion: 'Solo consulta: tablero e inventario.' },
  { id: 'Empleado', descripcion: 'Consulta básica y reportes del día.' },
  { id: 'Administrador', descripcion: 'Todo salvo la gestión de usuarios.' },
];

// Math.random() no es criptográficamente seguro y esto es una credencial.
function contrasenaSegura(largo = 12) {
  const alfabeto = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(largo);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => alfabeto[b % alfabeto.length]).join('');
}

const sinNumeros = /^[\p{L}\s'’-]*$/u;

function Credencial({ titulo, usuario, contrasena, alCerrar }) {
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(`Usuario: ${usuario}\nContraseña: ${contrasena}`);
      toast.success('Credenciales copiadas');
    } catch {
      toast.error('No se pudo copiar; selecciónalas a mano');
    }
  };
  return (
    <div className="credencial" role="status">
      <div className="credencial-texto">
        <h3>{titulo}</h3>
        <p className="texto-auxiliar">
          Esta es la única vez que se muestra. Cópiala y entrégasela a la persona.
        </p>
        <dl>
          <div><dt>Usuario</dt><dd>{usuario}</dd></div>
          <div><dt>Contraseña</dt><dd className="mono">{contrasena}</dd></div>
        </dl>
      </div>
      <div className="credencial-acciones">
        <button onClick={copiar}>Copiar</button>
        <button className="fantasma" onClick={alCerrar}>Ya la guardé</button>
      </div>
    </div>
  );
}

const Usuarios = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const yo = localStorage.getItem('authenticatedUser') || '';

  const [empleados, setEmpleados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState({ nombre: '', apellido: '', rol: 'Cajero' });
  const [editando, setEditando] = useState(null);
  const [credencial, setCredencial] = useState(null);
  const [porEliminar, setPorEliminar] = useState(null);
  const [aResetear, setAResetear] = useState(null);
  const [claveSuper, setClaveSuper] = useState('');
  const [reseteando, setReseteando] = useState(false);
  const refDialogo = useRef(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get(`${apiUrl}/api/empleados/empleados`);
      setEmpleados(Array.isArray(data) ? data : []);
    } catch {
      toast.error('No se pudieron cargar los empleados');
    } finally {
      setCargando(false);
    }
  }, [apiUrl]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!porEliminar && !aResetear) return;
    refDialogo.current?.querySelector('input, button')?.focus();
    const alPulsar = (e) => {
      if (e.key === 'Escape') { setPorEliminar(null); setAResetear(null); }
    };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [porEliminar, aResetear]);

  const guardar = async () => {
    if (!form.nombre.trim() || !form.apellido.trim()) {
      return toast.error('Nombre y apellido son obligatorios');
    }
    try {
      if (editando) {
        await axios.put(`${apiUrl}/api/empleados/empleados/${editando.empleado_id}`, form);
        toast.success('Empleado actualizado');
        setEditando(null);
      } else {
        const usuario = `${form.nombre.trim().toLowerCase()}.${form.apellido.trim().toLowerCase()}`
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
        if (empleados.some((e) => e.usuario === usuario)) {
          return toast.error(`El usuario "${usuario}" ya existe`);
        }
        const contrasena = contrasenaSegura();
        await axios.post(`${apiUrl}/api/empleados/empleados`, {
          ...form, usuario, contraseña: contrasena,
        });
        setCredencial({ titulo: 'Empleado creado', usuario, contrasena });
      }
      setForm({ nombre: '', apellido: '', rol: 'Cajero' });
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo guardar');
    }
  };

  const eliminar = async () => {
    const emp = porEliminar;
    setPorEliminar(null);
    try {
      await axios.delete(`${apiUrl}/api/empleados/empleados/${emp.empleado_id}`);
      toast.success('Empleado eliminado');
      cargar();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const confirmarReset = async () => {
    if (!claveSuper) return toast.error('Ingresa tu contraseña para confirmar');
    setReseteando(true);
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/empleados/empleados/${aResetear.empleado_id}/reset-password`,
        { superadminPassword: claveSuper }
      );
      setCredencial({
        titulo: 'Contraseña restablecida',
        usuario: aResetear.usuario,
        contrasena: data.contraseña,
      });
      setAResetear(null);
      setClaveSuper('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo restablecer');
    } finally {
      setReseteando(false);
    }
  };

  const editar = (emp) => {
    setEditando(emp);
    setCredencial(null);
    setForm({ nombre: emp.nombre, apellido: emp.apellido, rol: emp.rol });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const rolActual = ROLES.find((r) => r.id === form.rol);

  return (
    <div className="usuarios">
      <ToastContainer position="top-right" autoClose={2600} />

      <div className="encabezado-pagina">
        <div>
          <h1>Usuarios</h1>
          <p className="subtitulo">Empleados con acceso al sistema y sus permisos.</p>
        </div>
      </div>

      {credencial && (
        <Credencial {...credencial} alCerrar={() => setCredencial(null)} />
      )}

      <section className="tarjeta-base">
        <div className="encabezado-seccion">
          <h2>{editando ? `Editando a ${editando.nombre} ${editando.apellido}` : 'Nuevo empleado'}</h2>
        </div>

        <div className="form-usuario">
          <div className="campo">
            <label htmlFor="u-nombre">Nombre</label>
            <input id="u-nombre" type="text" value={form.nombre} placeholder="Andrea"
              onChange={(e) => sinNumeros.test(e.target.value) && setForm({ ...form, nombre: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="u-apellido">Apellido</label>
            <input id="u-apellido" type="text" value={form.apellido} placeholder="Gutiérrez"
              onChange={(e) => sinNumeros.test(e.target.value) && setForm({ ...form, apellido: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="u-rol">Rol</label>
            <select id="u-rol" value={form.rol}
              onChange={(e) => setForm({ ...form, rol: e.target.value })}>
              {ROLES.map((r) => <option key={r.id} value={r.id}>{r.id}</option>)}
            </select>
          </div>
          <div className="acciones-usuario">
            <button onClick={guardar}>{editando ? 'Guardar cambios' : 'Crear empleado'}</button>
            {editando && (
              <button className="cancelar" onClick={() => {
                setEditando(null); setForm({ nombre: '', apellido: '', rol: 'Cajero' });
              }}>Cancelar</button>
            )}
          </div>
        </div>

        {/* Los roles se explican: antes había que adivinar qué podía cada uno. */}
        {rolActual && <p className="ayuda-rol"><strong>{rolActual.id}:</strong> {rolActual.descripcion}</p>}
        {!editando && (
          <p className="texto-auxiliar">
            El usuario se genera como nombre.apellido y la contraseña se crea automáticamente.
          </p>
        )}
      </section>

      <section className="tarjeta-base">
        <div className="encabezado-seccion">
          <h2>Empleados</h2>
          <span className="texto-auxiliar">{empleados.length}</span>
        </div>

        {empleados.length === 0 ? (
          <div className="estado-vacio">
            <h3>{cargando ? 'Cargando…' : 'Sin empleados'}</h3>
          </div>
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr><th>Nombre</th><th>Usuario</th><th>Rol</th><th></th></tr>
              </thead>
              <tbody>
                {empleados.map((e) => {
                  const esYo = e.usuario === yo;
                  return (
                    <tr key={e.empleado_id}>
                      <td>
                        {e.nombre} {e.apellido}
                        {esYo && <span className="distintivo neutro marca-yo">Tú</span>}
                      </td>
                      <td className="mono">{e.usuario}</td>
                      <td><span className="distintivo neutro">{e.rol}</span></td>
                      <td>
                        <div className="acciones-fila">
                          <button className="secundario pequeno" onClick={() => editar(e)}>Editar</button>
                          <button className="secundario pequeno" onClick={() => { setAResetear(e); setClaveSuper(''); }}>
                            Restablecer clave
                          </button>
                          <button className="borrar-button pequeno" disabled={esYo}
                            title={esYo ? 'No puedes eliminar tu propio usuario' : undefined}
                            onClick={() => setPorEliminar(e)}>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {porEliminar && (
        <div className="modal-usr-overlay"
          onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setPorEliminar(null); }}>
          <div className="modal-usr" ref={refDialogo} role="dialog" aria-modal="true">
            <h3>¿Eliminar a {porEliminar.nombre} {porEliminar.apellido}?</h3>
            <p className="texto-auxiliar">
              Perderá el acceso al sistema de inmediato. Las ventas que ya registró se conservan.
            </p>
            <div className="fila-fin">
              <button className="cancelar" onClick={() => setPorEliminar(null)}>Cancelar</button>
              <button className="borrar-button" onClick={eliminar}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}

      {aResetear && (
        <div className="modal-usr-overlay"
          onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setAResetear(null); }}>
          <div className="modal-usr" ref={refDialogo} role="dialog" aria-modal="true">
            <h3>Restablecer contraseña</h3>
            <p className="texto-auxiliar">
              Se generará una contraseña nueva para <strong>{aResetear.usuario}</strong>.
              Confirma con tu propia contraseña.
            </p>
            <div className="campo">
              <label htmlFor="clave-super">Tu contraseña</label>
              <input id="clave-super" type="password" value={claveSuper}
                autoComplete="current-password"
                onChange={(ev) => setClaveSuper(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === 'Enter') confirmarReset(); }} />
            </div>
            <div className="fila-fin">
              <button className="cancelar" onClick={() => setAResetear(null)}>Cancelar</button>
              <button onClick={confirmarReset} disabled={reseteando} data-ocupado={reseteando}>
                {reseteando ? 'Confirmando…' : 'Restablecer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
